// ============================================================================
// Input Component
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Base input component with variants
// Changed: 2026-10-30, spec 046 T045: the helper text or error message describes its field
//          (aria-describedby), and an error marks the field invalid (aria-invalid)
// ============================================================================

import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { clsx } from 'clsx';

/**
 * Input sizes
 */
export type InputSize = 'sm' | 'md' | 'lg';

/**
 * Input state
 */
export type InputState = 'default' | 'error' | 'success' | 'warning';

/**
 * Input props
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  state?: InputState;
  error?: boolean; // Alias for state="error"
  label?: string;
  helperText?: string;
  errorMessage?: string;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  isFullWidth?: boolean;
}

/**
 * Base styles for inputs
 */
const baseStyles = `
  block rounded-[var(--radius-md)] border
  bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
  transition-colors duration-[120ms]
  focus:outline-none focus:ring-2 focus:ring-offset-0
  disabled:opacity-50 disabled:cursor-not-allowed
  placeholder:text-[var(--color-text-secondary)]
`;

/**
 * State styles
 */
const stateStyles: Record<InputState, string> = {
  default: `
    border-[var(--color-border)]
    focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]
  `,
  error: `
    border-red-500 
    focus:border-red-500 focus:ring-red-500
    dark:border-red-400
  `,
  success: `
    border-green-500 
    focus:border-green-500 focus:ring-green-500
    dark:border-green-400
  `,
  warning: `
    border-yellow-500 
    focus:border-yellow-500 focus:ring-yellow-500
    dark:border-yellow-400
  `,
};

/**
 * Size styles
 */
const sizeStyles: Record<InputSize, string> = {
  sm: 'text-sm px-3 py-1.5',
  md: 'text-sm px-4 py-2',
  lg: 'text-base px-4 py-2.5',
};

/**
 * Input Component
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      inputSize = 'md',
      state = 'default',
      error,
      label,
      helperText,
      errorMessage,
      leftAddon,
      rightAddon,
      isFullWidth = false,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const messageId = helperText || errorMessage ? `${inputId}-message` : undefined;
    const effectiveState = errorMessage || error ? 'error' : state;

    return (
      <div className={clsx('flex flex-col', isFullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 text-sm font-medium text-[var(--color-text-primary)]"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leftAddon && (
            <div className="absolute left-3 flex items-center text-gray-400">
              {leftAddon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-describedby={messageId}
            aria-invalid={errorMessage || error ? true : undefined}
            className={clsx(
              baseStyles,
              stateStyles[effectiveState],
              sizeStyles[inputSize],
              leftAddon && 'pl-10',
              rightAddon && 'pr-10',
              isFullWidth && 'w-full',
              className
            )}
            {...props}
          />
          {rightAddon && (
            <div className="absolute right-3 flex items-center text-gray-400">
              {rightAddon}
            </div>
          )}
        </div>
        {(helperText || errorMessage) && (
          <p
            id={messageId}
            className={clsx(
              'mt-1.5 text-sm',
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-[var(--color-text-secondary)]'
            )}
          >
            {errorMessage || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

/**
 * Textarea Component
 */
export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  inputSize?: InputSize;
  state?: InputState;
  error?: boolean; // Alias for state="error"
  label?: string;
  helperText?: string;
  errorMessage?: string;
  isFullWidth?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      inputSize = 'md',
      state = 'default',
      error,
      label,
      helperText,
      errorMessage,
      isFullWidth = false,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const messageId = helperText || errorMessage ? `${inputId}-message` : undefined;
    const effectiveState = errorMessage || error ? 'error' : state;

    return (
      <div className={clsx('flex flex-col', isFullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 text-sm font-medium text-[var(--color-text-primary)]"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          aria-describedby={messageId}
          aria-invalid={errorMessage || error ? true : undefined}
          className={clsx(
            baseStyles,
            stateStyles[effectiveState],
            sizeStyles[inputSize],
            'min-h-[100px] resize-y',
            isFullWidth && 'w-full',
            className
          )}
          {...props}
        />
        {(helperText || errorMessage) && (
          <p
            id={messageId}
            className={clsx(
              'mt-1.5 text-sm',
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-[var(--color-text-secondary)]'
            )}
          >
            {errorMessage || helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Input;
